import { toast } from "sonner";
import { Copy, Link2, Lock, CheckCircle2, ShieldCheck, Clock } from "lucide-react";
import { publicQuotationUrl } from "@/lib/quotationApi";
import ActivityTimeline from "@/components/ActivityTimeline";

export default function SummaryShareCards({
  savedId,
  savedStatus,
  canApprove,
  onApprove,
  onUnapprove,
  approving,
  showApprovalTrail,
  approvedByName,
  approvedAt,
  activity,
  readOnly,
}) {
  const copyLink = async () => {
    if (!savedId) return;
    const url = publicQuotationUrl(savedId);
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied");
    } catch {
      toast.error("Copy failed");
    }
  };

  return (
    <>
      {/* Public link — only available once the quote is approved */}
      {savedId && savedStatus === "approved" && (
        <div className="card-luxe mb-4" data-testid="shareable-link-card">
          <div className="flex items-center justify-between mb-2">
            <div className="overline text-[#C5A880] flex items-center gap-1.5">
              <Link2 size={11} /> Shareable link
            </div>
            <span
              className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-emerald-300"
              data-testid="approved-pill"
            >
              <CheckCircle2 size={12} /> Approved · live
            </span>
          </div>
          <div className="flex items-center gap-2">
            <div
              className="flex-1 min-w-0 truncate text-xs text-white/70 bg-white/[0.03] border border-white/10 rounded-lg px-3 py-2.5 font-mono"
              data-testid="public-link"
            >
              {publicQuotationUrl(savedId)}
            </div>
            <button
              onClick={copyLink}
              className="btn-ghost !h-10 !px-3"
              data-testid="copy-link-btn"
            >
              <Copy size={14} />
            </button>
          </div>
          <div className="flex items-center justify-between mt-2">
            <p className="text-[11px] text-white/40">
              Customer can open this link from WhatsApp to view the quotation anytime.
            </p>
            {canApprove && !readOnly && (
              <button
                onClick={onUnapprove}
                disabled={approving}
                className="text-[11px] text-white/40 hover:text-red-300 disabled:opacity-50 shrink-0 ml-3"
                data-testid="unapprove-btn"
              >
                {approving ? "Working…" : "Remove approval"}
              </button>
            )}
          </div>
          {showApprovalTrail && approvedByName && (
            <div
              className="mt-2 pt-2 border-t border-white/5 flex items-center gap-1.5 text-[11px] text-[#C5A880]/90"
              data-testid="approval-trail"
            >
              <ShieldCheck size={12} /> Approved by {approvedByName}
              {approvedAt && (
                <> · {new Date(approvedAt).toLocaleString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "numeric", minute: "2-digit" })}</>
              )}
            </div>
          )}
        </div>
      )}

      {/* Not-yet-approved notice: no public link until approved */}
      {savedId && savedStatus !== "approved" && (
        <div
          className="rounded-xl border border-amber-400/25 bg-amber-400/[0.06] px-4 py-3 mb-4"
          data-testid="not-approved-notice"
        >
          <div className="overline text-amber-300/90 mb-1 flex items-center gap-1.5">
            <Lock size={11} /> Not shareable yet
          </div>
          <p className="text-[12px] text-white/60 leading-snug">
            The customer link is generated only after this quotation is
            <span className="text-amber-200/90 font-medium"> approved</span>.
            {canApprove
              ? " Approve it below to publish the link."
              : " Ask an admin to approve it (or set its status to Approved from the History page)."}{" "}
            You can still download an internal copy below — it will be marked{" "}
            <span className="font-medium">&quot;NOT APPROVED&quot;</span>.
          </p>
          {canApprove && !readOnly && (
            <button
              onClick={onApprove}
              disabled={approving}
              className="btn-primary flex items-center justify-center gap-2 mt-3 w-full sm:w-auto disabled:opacity-60"
              data-testid="approve-btn"
            >
              <CheckCircle2 size={16} />
              {approving ? "Approving…" : "Approve & publish"}
            </button>
          )}
        </div>
      )}

      {/* Activity timeline — manager/admin audit view */}
      {showApprovalTrail && savedId && (
        <div className="card-luxe mb-4" data-testid="activity-card">
          <div className="overline text-[#C5A880] mb-3 flex items-center gap-1.5">
            <Clock size={11} /> Activity
          </div>
          <ActivityTimeline activity={activity} testId="step6-activity" />
        </div>
      )}
    </>
  );
}
